/**
 * Integration Monitoring and Analytics Dashboard
 * Provides real-time monitoring, performance metrics, and troubleshooting interface
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  RefreshCw,
  Settings,
  Eye,
  Download,
  Search,
  Bell,
  BarChart3,
  Zap,
  Database,
  Users,
  AlertCircle,
  Info,
  XCircle
} from 'lucide-react';
import { tone, toneForStatus } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

// Types for monitoring data
interface IntegrationHealth {
  id: string;
  name: string;
  type: 'crm' | 'ecommerce' | 'social';
  platform: string;
  status: 'healthy' | 'warning' | 'error' | 'offline';
  healthScore: number;
  lastSync: Date;
  syncSuccess: number;
  errorRate: number;
  responseTime: number;
  uptime: number;
  recordsProcessed: number;
  recordsPerHour: number;
}

interface SyncOperation {
  id: string;
  integrationId: string;
  integrationName: string;
  type: 'full_sync' | 'incremental_sync' | 'real_time_update';
  direction: 'bidirectional' | 'to_platform' | 'from_platform';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: Date;
  completedAt?: Date;
  duration?: number;
  recordsProcessed: number;
  recordsSucceeded: number;
  recordsFailed: number;
  errors: SyncError[];
  progress?: number;
}

interface SyncError {
  id: string;
  timestamp: Date;
  level: 'error' | 'warning' | 'info';
  message: string;
  details?: string;
  recordId?: string;
  field?: string;
  resolution?: string;
  resolved: boolean;
}

interface PerformanceMetrics {
  totalIntegrations: number;
  activeIntegrations: number;
  healthyIntegrations: number;
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageResponseTime: number;
  recordsProcessedToday: number;
  errorRate: number;
  uptime: number;
}

interface AlertRule {
  id: string;
  name: string;
  condition: string;
  threshold: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  enabled: boolean;
  lastTriggered?: Date;
}

const IntegrationMonitoringDashboard: React.FC = () => {
  const [integrations, setIntegrations] = useState<IntegrationHealth[]>([]);
  const [syncOperations, setSyncOperations] = useState<SyncOperation[]>([]);
  const [errors, setErrors] = useState<SyncError[]>([]);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedIntegration, setSelectedIntegration] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'operations' | 'errors' | 'alerts'>('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Mock data - in real implementation, this would come from API
  const mockIntegrations: IntegrationHealth[] = [
    {
      id: 'salesforce-1',
      name: 'Salesforce CRM',
      type: 'crm',
      platform: 'salesforce',
      status: 'healthy',
      healthScore: 98,
      lastSync: new Date(Date.now() - 1000 * 60 * 5), // 5 minutes ago
      syncSuccess: 99.2,
      errorRate: 0.8,
      responseTime: 245,
      uptime: 99.9,
      recordsProcessed: 15420,
      recordsPerHour: 1250
    },
    {
      id: 'hubspot-1',
      name: 'HubSpot CRM',
      type: 'crm',
      platform: 'hubspot',
      status: 'warning',
      healthScore: 85,
      lastSync: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
      syncSuccess: 94.5,
      errorRate: 5.5,
      responseTime: 890,
      uptime: 98.2,
      recordsProcessed: 8930,
      recordsPerHour: 720
    },
    {
      id: 'shopify-1',
      name: 'Shopify Store',
      type: 'ecommerce',
      platform: 'shopify',
      status: 'healthy',
      healthScore: 96,
      lastSync: new Date(Date.now() - 1000 * 60 * 2), // 2 minutes ago
      syncSuccess: 98.8,
      errorRate: 1.2,
      responseTime: 320,
      uptime: 99.7,
      recordsProcessed: 23450,
      recordsPerHour: 1890
    },
    {
      id: 'facebook-1',
      name: 'Facebook & Instagram',
      type: 'social',
      platform: 'facebook',
      status: 'error',
      healthScore: 45,
      lastSync: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
      syncSuccess: 78.3,
      errorRate: 21.7,
      responseTime: 1250,
      uptime: 95.1,
      recordsProcessed: 5670,
      recordsPerHour: 340
    }
  ];

  const mockSyncOperations: SyncOperation[] = [
    {
      id: 'sync-1',
      integrationId: 'salesforce-1',
      integrationName: 'Salesforce CRM',
      type: 'incremental_sync',
      direction: 'bidirectional',
      status: 'running',
      startedAt: new Date(Date.now() - 1000 * 60 * 3),
      recordsProcessed: 1250,
      recordsSucceeded: 1248,
      recordsFailed: 2,
      errors: [],
      progress: 75
    },
    {
      id: 'sync-2',
      integrationId: 'hubspot-1',
      integrationName: 'HubSpot CRM',
      type: 'full_sync',
      direction: 'from_platform',
      status: 'completed',
      startedAt: new Date(Date.now() - 1000 * 60 * 45),
      completedAt: new Date(Date.now() - 1000 * 60 * 30),
      duration: 15 * 60 * 1000, // 15 minutes
      recordsProcessed: 8930,
      recordsSucceeded: 8845,
      recordsFailed: 85,
      errors: [
        {
          id: 'error-1',
          timestamp: new Date(Date.now() - 1000 * 60 * 35),
          level: 'warning',
          message: 'Rate limit exceeded, retrying with backoff',
          details: 'HubSpot API rate limit of 100 requests per 10 seconds exceeded',
          resolved: true,
          resolution: 'Automatically retried after 30 seconds'
        }
      ]
    },
    {
      id: 'sync-3',
      integrationId: 'facebook-1',
      integrationName: 'Facebook & Instagram',
      type: 'real_time_update',
      direction: 'to_platform',
      status: 'failed',
      startedAt: new Date(Date.now() - 1000 * 60 * 120),
      completedAt: new Date(Date.now() - 1000 * 60 * 118),
      duration: 2 * 60 * 1000, // 2 minutes
      recordsProcessed: 45,
      recordsSucceeded: 0,
      recordsFailed: 45,
      errors: [
        {
          id: 'error-2',
          timestamp: new Date(Date.now() - 1000 * 60 * 118),
          level: 'error',
          message: 'Authentication failed',
          details: 'Facebook access token has expired and needs to be refreshed',
          resolved: false
        }
      ]
    }
  ];

  const mockMetrics: PerformanceMetrics = {
    totalIntegrations: 4,
    activeIntegrations: 3,
    healthyIntegrations: 2,
    totalSyncs: 156,
    successfulSyncs: 142,
    failedSyncs: 14,
    averageResponseTime: 426,
    recordsProcessedToday: 52470,
    errorRate: 8.97,
    uptime: 98.2
  };

  const mockAlertRules: AlertRule[] = [
    {
      id: 'alert-1',
      name: 'High Error Rate',
      condition: 'error_rate > threshold',
      threshold: 10,
      severity: 'high',
      enabled: true,
      lastTriggered: new Date(Date.now() - 1000 * 60 * 30)
    },
    {
      id: 'alert-2',
      name: 'Sync Failure',
      condition: 'sync_failed',
      threshold: 1,
      severity: 'critical',
      enabled: true,
      lastTriggered: new Date(Date.now() - 1000 * 60 * 120)
    },
    {
      id: 'alert-3',
      name: 'Response Time Degradation',
      condition: 'response_time > threshold',
      threshold: 1000,
      severity: 'medium',
      enabled: true
    }
  ];

  useEffect(() => {
    loadMonitoringData();
  }, [selectedTimeRange, selectedIntegration]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadMonitoringData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, selectedTimeRange, selectedIntegration]);

  const loadMonitoringData = useCallback(async () => {
    try {
      setLoading(true);
      
      // In real implementation, these would be API calls
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
      
      setIntegrations(mockIntegrations);
      setSyncOperations(mockSyncOperations);
      setMetrics(mockMetrics);
      setAlertRules(mockAlertRules);
      
      // Extract errors from sync operations
      const allErrors = mockSyncOperations.flatMap(op => op.errors);
      setErrors(allErrors);
      
    } catch (error) {
      console.error('Failed to load monitoring data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange, selectedIntegration]);

  const getStatusColor = (status) => tone(toneForStatus(status)).chip;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <XCircle className="w-4 h-4" />;
      case 'offline': return <AlertCircle className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getPlatformIcon = (type: string) => {
    switch (type) {
      case 'crm': return <Users className="w-5 h-5" />;
      case 'ecommerce': return <Database className="w-5 h-5" />;
      case 'social': return <Activity className="w-5 h-5" />;
      default: return <Zap className="w-5 h-5" />;
    }
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading && integrations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-300" />
        <span className="ml-2 text-content-secondary">Loading monitoring data...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-content">Integration Monitoring</h1>
          <p className="text-content-secondary mt-1">
            Real-time monitoring and analytics for all platform integrations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-content-secondary">Auto-refresh:</label>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1 rounded text-sm ${
                autoRefresh 
                  ? 'bg-green-100 text-green-700 dark:text-green-300' 
                  : 'bg-gray-100 dark:bg-dark-700 text-content-secondary'
              }`}
            >
              {autoRefresh ? 'On' : 'Off'}
            </button>
          </div>
          <SelectField
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-3 py-2 border border-line-strong rounded-lg text-sm"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </SelectField>
          <button
            onClick={loadMonitoringData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Metrics Overview */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-surface p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-content-secondary">Active Integrations</p>
                <p className="text-2xl font-bold text-content">
                  {metrics.activeIntegrations}/{metrics.totalIntegrations}
                </p>
              </div>
              <Activity className="w-8 h-8 text-blue-600 dark:text-blue-300" />
            </div>
            <div className="mt-2">
              <span className="text-sm text-green-600 dark:text-green-300">
                {metrics.healthyIntegrations} healthy
              </span>
            </div>
          </div>

          <div className="bg-surface p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-content-secondary">Success Rate</p>
                <p className="text-2xl font-bold text-content">
                  {((metrics.successfulSyncs / metrics.totalSyncs) * 100).toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-300" />
            </div>
            <div className="mt-2">
              <span className="text-sm text-content-secondary">
                {metrics.successfulSyncs}/{metrics.totalSyncs} syncs
              </span>
            </div>
          </div>

          <div className="bg-surface p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-content-secondary">Avg Response Time</p>
                <p className="text-2xl font-bold text-content">{metrics.averageResponseTime}ms</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-600 dark:text-purple-300" />
            </div>
            <div className="mt-2">
              <span className={`text-sm ${metrics.averageResponseTime > 500 ? 'text-red-600 dark:text-red-300' : 'text-green-600 dark:text-green-300'}`}>
                {metrics.averageResponseTime > 500 ? 'Slow' : 'Good'}
              </span>
            </div>
          </div>

          <div className="bg-surface p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-content-secondary">Records Today</p>
                <p className="text-2xl font-bold text-content">
                  {metrics.recordsProcessedToday.toLocaleString()}
                </p>
              </div>
              <Database className="w-8 h-8 text-indigo-600 dark:text-indigo-300" />
            </div>
            <div className="mt-2">
              <span className="text-sm text-content-secondary">
                {Math.round(metrics.recordsProcessedToday / 24)}/hr avg
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-line mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'operations', label: 'Sync Operations', icon: RefreshCw },
            { id: 'errors', label: 'Errors & Issues', icon: AlertTriangle },
            { id: 'alerts', label: 'Alert Rules', icon: Bell }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Integration Health Cards */}
          <div>
            <h2 className="text-xl font-semibold text-content mb-4">Integration Health</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {integrations.map((integration) => (
                <div key={integration.id} className="bg-surface p-6 rounded-lg shadow-sm border">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getPlatformIcon(integration.type)}
                      <div>
                        <h3 className="font-semibold text-content">{integration.name}</h3>
                        <p className="text-sm text-content-secondary capitalize">{integration.type} Integration</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(integration.status)}`}>
                        {getStatusIcon(integration.status)}
                        {integration.status}
                      </span>
                      <div className="text-right">
                        <div className="text-lg font-bold text-content">{integration.healthScore}%</div>
                        <div className="text-xs text-content-muted">Health Score</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-content-secondary">Success Rate</p>
                      <p className="font-medium">{integration.syncSuccess}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-content-secondary">Response Time</p>
                      <p className="font-medium">{integration.responseTime}ms</p>
                    </div>
                    <div>
                      <p className="text-sm text-content-secondary">Records/Hour</p>
                      <p className="font-medium">{integration.recordsPerHour.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-content-secondary">
                      Last sync: {formatTimeAgo(integration.lastSync)}
                    </span>
                    <span className="text-content-secondary">
                      Uptime: {integration.uptime}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'operations' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-content-subtle" />
                <input
                  type="text"
                  placeholder="Search operations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-line-strong rounded-lg w-full"
                />
              </div>
            </div>
            <SelectField
              value={selectedIntegration}
              onChange={(e) => setSelectedIntegration(e.target.value)}
              className="px-3 py-2 border border-line-strong rounded-lg"
            >
              <option value="all">All Integrations</option>
              {integrations.map((integration) => (
                <option key={integration.id} value={integration.id}>
                  {integration.name}
                </option>
              ))}
            </SelectField>
          </div>

          {/* Sync Operations Table */}
          <div className="bg-surface rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-medium text-content">Recent Sync Operations</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Integration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Started
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-gray-200">
                  {syncOperations.map((operation) => (
                    <tr key={operation.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-content">
                          {operation.integrationName}
                        </div>
                        <div className="text-sm text-content-muted">
                          {operation.direction}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 rounded">
                          {operation.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${getStatusColor(operation.status)}`}>
                          {getStatusIcon(operation.status)}
                          {operation.status}
                        </span>
                        {operation.status === 'running' && operation.progress && (
                          <div className="mt-1 w-full bg-gray-200 dark:bg-dark-600 rounded-full h-1">
                            <div 
                              className="bg-blue-600 h-1 rounded-full" 
                              style={{ width: `${operation.progress}%` }}
                            ></div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                        <div>{operation.recordsProcessed.toLocaleString()} processed</div>
                        <div className="text-xs text-content-muted">
                          {operation.recordsSucceeded.toLocaleString()} success, {operation.recordsFailed.toLocaleString()} failed
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                        {operation.duration ? formatDuration(operation.duration) : 
                         operation.status === 'running' ? formatDuration(Date.now() - operation.startedAt.getTime()) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content-muted">
                        {formatTimeAgo(operation.startedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button aria-label="View details" className="text-blue-600 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-300 mr-3">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button aria-label="Download" className="text-content-secondary hover:text-content">
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'errors' && (
        <div className="space-y-6">
          {/* Error Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Total Errors</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-300">{errors.length}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-300" />
              </div>
            </div>
            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Unresolved</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-300">
                    {errors.filter(e => !e.resolved).length}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-600 dark:text-yellow-300" />
              </div>
            </div>
            <div className="bg-surface p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Resolved</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-300">
                    {errors.filter(e => e.resolved).length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300" />
              </div>
            </div>
          </div>

          {/* Error List */}
          <div className="bg-surface rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-medium text-content">Recent Errors</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {errors.map((error) => (
                <div key={error.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          error.level === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          error.level === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        }`}>
                          {error.level.toUpperCase()}
                        </span>
                        <span className="text-sm text-content-muted">
                          {formatTimeAgo(error.timestamp)}
                        </span>
                        {error.resolved && (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded text-xs font-medium">
                            RESOLVED
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-content mb-1">{error.message}</h4>
                      {error.details && (
                        <p className="text-sm text-content-secondary mb-2">{error.details}</p>
                      )}
                      {error.resolution && (
                        <p className="text-sm text-green-600 dark:text-green-300">
                          <strong>Resolution:</strong> {error.resolution}
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      {!error.resolved && (
                        <button className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium">
                          Mark Resolved
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-6">
          {/* Alert Rules */}
          <div className="bg-surface rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-line flex items-center justify-between">
              <h3 className="text-lg font-medium text-content">Alert Rules</h3>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">
                Add Alert Rule
              </button>
            </div>
            <div className="divide-y divide-gray-200">
              {alertRules.map((rule) => (
                <div key={rule.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium text-content">{rule.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          rule.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          rule.severity === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                          rule.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        }`}>
                          {rule.severity.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200'
                        }`}>
                          {rule.enabled ? 'ENABLED' : 'DISABLED'}
                        </span>
                      </div>
                      <p className="text-sm text-content-secondary mb-1">
                        {rule.condition} ({rule.threshold})
                      </p>
                      {rule.lastTriggered && (
                        <p className="text-sm text-content-muted">
                          Last triggered: {formatTimeAgo(rule.lastTriggered)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button aria-label="Settings" className="text-content-secondary hover:text-gray-800 dark:hover:text-gray-200">
                        <Settings className="w-4 h-4" />
                      </button>
                      <button 
                        className={`px-3 py-1 rounded text-sm ${
                          rule.enabled 
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/40' 
                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/40'
                        }`}
                      >
                        {rule.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationMonitoringDashboard;